/**
 * CreateNewMenu - Global "Create New" dropdown button for Admin header
 * 
 * RBAC-aware menu with grouped sections:
 * - Sales: New Order
 * - Customers: New Customer
 * - Expenses: New Purchase Order, New Vendor Bill, New Payment
 * - Catalog: New Product, New Vendor
 * 
 * Now uses CreateNewContext for unified modal management
 */
import React, { useState, useRef, useEffect } from 'react';
import { Button } from '../ui/button';
import {
  Plus,
  ShoppingCart,
  Building2,
  FileText,
  Receipt,
  Package,
  Factory,
  ChevronDown,
  User,
  DollarSign,
} from 'lucide-react';
import { useCreateNew, MODAL_KEYS } from './CreateNewContext';

const CreateNewMenu = ({ permissions }) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef(null);
  const { openCreateModal } = useCreateNew();

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Get section permissions (default to all visible for admin)
  const sections = permissions?.sections || {
    order_management: { editable: true },
    crm: { editable: true },
    expenses: { editable: true },
    master_data: { editable: true },
  };

  // Check individual permissions
  const canCreateOrder = sections.order_management?.editable;
  const canCreateCustomer = sections.crm?.editable;
  const canCreatePO = sections.expenses?.editable;
  const canCreateBill = sections.expenses?.editable;
  const canCreateProduct = sections.master_data?.editable;
  const canCreateVendor = sections.expenses?.editable;

  // Build menu items based on permissions
  const menuItems = [];

  // Sales section
  if (canCreateOrder) {
    menuItems.push({
      section: 'Sales',
      items: [
        { label: 'New Order', icon: ShoppingCart, onClick: () => openCreateModal(MODAL_KEYS.ORDER), testId: 'create-new-order' },
      ],
    });
  }

  // Customers section
  if (canCreateCustomer) {
    menuItems.push({
      section: 'Customers',
      items: [
        { label: 'New Customer', icon: Building2, onClick: () => openCreateModal(MODAL_KEYS.CUSTOMER), testId: 'create-new-customer' },
        { label: 'New Contact', icon: User, onClick: () => openCreateModal(MODAL_KEYS.CONTACT), testId: 'create-new-contact' },
      ],
    });
  }

  // Expenses section
  const expenseItems = [];
  if (canCreatePO) {
    expenseItems.push({ label: 'New Purchase Order', icon: FileText, onClick: () => openCreateModal(MODAL_KEYS.PURCHASE_ORDER), testId: 'create-new-po' });
  }
  if (canCreateBill) {
    expenseItems.push({ label: 'New Vendor Bill', icon: Receipt, onClick: () => openCreateModal(MODAL_KEYS.BILL), testId: 'create-new-bill' });
  }
  // Payment - available to anyone who can create bills
  if (canCreateBill) {
    expenseItems.push({ label: 'New Payment', icon: DollarSign, onClick: () => openCreateModal(MODAL_KEYS.PAYMENT), testId: 'create-new-payment' });
  }
  if (expenseItems.length > 0) {
    menuItems.push({ section: 'Expenses', items: expenseItems });
  }

  // Catalog section
  const catalogItems = [];
  if (canCreateProduct) {
    catalogItems.push({ label: 'New Product', icon: Package, onClick: () => openCreateModal(MODAL_KEYS.PRODUCT), testId: 'create-new-product' });
  }
  if (canCreateVendor) {
    catalogItems.push({ label: 'New Vendor', icon: Factory, onClick: () => openCreateModal(MODAL_KEYS.VENDOR), testId: 'create-new-vendor' });
  }
  if (catalogItems.length > 0) {
    menuItems.push({ section: 'Catalog', items: catalogItems });
  }

  // If no items visible, don't render menu
  if (menuItems.length === 0) return null;

  const handleItemClick = (onClick) => {
    setIsOpen(false);
    onClick?.();
  };

  return (
    <div className="relative" ref={menuRef}>
      <Button
        onClick={() => setIsOpen(!isOpen)}
        className="bg-gold-600 hover:bg-gold-700 text-white gap-2"
        data-testid="create-new-btn"
      >
        <Plus className="w-4 h-4" />
        Create New
        <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </Button>

      {isOpen && (
        <div 
          className="absolute left-0 top-full mt-2 w-56 bg-white border border-ivory-200 rounded-lg shadow-lg z-50"
          data-testid="create-new-menu"
        >
          {menuItems.map((group, groupIdx) => (
            <div key={group.section}>
              {/* Section header */}
              <div className="px-3 py-2 text-xs font-semibold text-ivory-500 uppercase tracking-wider bg-ivory-50">
                {group.section}
              </div>
              {/* Section items */}
              {group.items.map((item) => {
                const Icon = item.icon;
                return (
                  <button
                    key={item.label}
                    onClick={() => handleItemClick(item.onClick)}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-ivory-700 hover:bg-ivory-50 hover:text-ivory-900 transition-colors"
                    data-testid={item.testId}
                  >
                    <Icon className="w-4 h-4 text-ivory-500" />
                    {item.label}
                  </button>
                );
              })}
              {/* Divider (except for last group) */}
              {groupIdx < menuItems.length - 1 && (
                <div className="border-t border-ivory-100" />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CreateNewMenu;
